/**
 * ChatArea — middle column in the Support Center. Renders the chat header
 * (with the dropdown actions), the messages list, and the message input.
 * Empty state is rendered when no contact is selected.
 * Extracted from Support.js during the iter-238 refactor (Feb 2026).
 */
import React from 'react';
import {
  MessageCircle, User, UserPlus, Info, MoreVertical, ArrowLeftRight,
  Loader2, CheckCircle2, XCircle, FileCheck2, Archive, Trash2, Send,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { ChannelIcon, StatusBadge, channelOf } from './atoms';
import { MessageBubble } from './MessageBubble';

export function ChatArea({
  isAr, t,
  selectedContact, messages, messagesEndRef,
  newMessage, setNewMessage, sending, sendMessage,
  assignToMe, showDetailsPanel, setShowDetailsPanel,
  setShowTransfer, updateStatus, sendToAudit, setConfirmDialog,
}) {
  return (
    <div className="flex-1 flex flex-col">
      {selectedContact ? (
        <>
          {/* Chat Header */}
          <div className="p-4 border-b border-[#2a2a4e] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#25D366]/20 flex items-center justify-center">
                <User className="h-5 w-5 text-[#25D366]" />
              </div>
              <div>
                <p className="font-semibold text-white flex items-center gap-2">
                  {selectedContact.name}
                  <ChannelIcon channel={channelOf(selectedContact)} size={14} showLabel />
                </p>
                <p className="text-xs text-[#808080]" dir="ltr">+{selectedContact.phone}</p>
              </div>
              <StatusBadge status={selectedContact.status || 'new'} isAr={isAr} />
            </div>

            <div className="flex items-center gap-2">
              {!selectedContact.assigned_to && (
                <Button
                  size="sm"
                  onClick={() => assignToMe(selectedContact.phone)}
                  className="bg-sky-600 hover:bg-sky-700 text-white"
                >
                  <UserPlus className="h-4 w-4 me-1" />
                  {t('استلام', 'Claim')}
                </Button>
              )}

              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowDetailsPanel((v) => !v)}
                className={`border-[#2a2a4e] hover:bg-white/5 ${showDetailsPanel ? 'text-[#d4af37]' : 'text-[#A1A1AA]'}`}
                title={t('بيانات العميل', 'Customer details')}
                data-testid="toggle-details-panel-btn"
              >
                <Info className="h-4 w-4" />
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="border-[#2a2a4e] text-white">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#1a1a2e] border-[#2a2a4e]">
                  <DropdownMenuItem
                    onClick={() => setShowTransfer(true)}
                    className="text-white hover:bg-[#2a2a4e] cursor-pointer"
                    data-testid="transfer-btn"
                  >
                    <ArrowLeftRight className="h-4 w-4 me-2" />
                    {t('تحويل لمشرف آخر', 'Transfer to another admin')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-[#2a2a4e]" />
                  <DropdownMenuItem
                    onClick={() => updateStatus(selectedContact.phone, 'in_progress')}
                    className="text-sky-400 hover:bg-[#2a2a4e] cursor-pointer"
                  >
                    <Loader2 className="h-4 w-4 me-2" />
                    {t('قيد المعالجة', 'In Progress')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => updateStatus(selectedContact.phone, 'resolved')}
                    className="text-emerald-400 hover:bg-[#2a2a4e] cursor-pointer"
                  >
                    <CheckCircle2 className="h-4 w-4 me-2" />
                    {t('تم الحل', 'Resolved')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => updateStatus(selectedContact.phone, 'closed')}
                    className="text-gray-400 hover:bg-[#2a2a4e] cursor-pointer"
                  >
                    <XCircle className="h-4 w-4 me-2" />
                    {t('إغلاق', 'Close')}
                  </DropdownMenuItem>
                  {(selectedContact.status === 'closed' || selectedContact.status === 'resolved') && (
                    <>
                      <DropdownMenuSeparator className="bg-[#2a2a4e]" />
                      <DropdownMenuItem
                        onClick={() => sendToAudit(selectedContact.phone)}
                        className="text-amber-400 hover:bg-[#2a2a4e] cursor-pointer"
                        data-testid="send-to-audit-btn"
                      >
                        <FileCheck2 className="h-4 w-4 me-2" />
                        {t('إرسال للتدقيق', 'Send to Audit')}
                      </DropdownMenuItem>
                    </>
                  )}
                  {/* iter-237 — Archive (soft) + Delete (permanent). */}
                  <DropdownMenuSeparator className="bg-[#2a2a4e]" />
                  <DropdownMenuItem
                    onClick={() => setConfirmDialog({ type: 'archive', phone: selectedContact.phone, name: selectedContact.name })}
                    className="text-indigo-300 hover:bg-[#2a2a4e] cursor-pointer"
                    data-testid="archive-btn"
                  >
                    <Archive className="h-4 w-4 me-2" />
                    {t('أرشفة المحادثة', 'Archive conversation')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setConfirmDialog({ type: 'delete', phone: selectedContact.phone, name: selectedContact.name })}
                    className="text-red-400 hover:bg-red-500/10 cursor-pointer"
                    data-testid="delete-btn"
                  >
                    <Trash2 className="h-4 w-4 me-2" />
                    {t('حذف المحادثة نهائياً', 'Delete conversation permanently')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((msg) => (
              <MessageBubble
                key={msg.id}
                msg={msg}
                selectedContact={selectedContact}
                isAr={isAr}
                t={t}
              />
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input */}
          <form onSubmit={sendMessage} className="p-4 border-t border-[#2a2a4e]">
            <div className="flex gap-3">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={t('اكتب رسالتك...', 'Type your message…')}
                className="flex-1 bg-[#0a0a0a] border-[#2a2a4e] text-white placeholder:text-[#606060] rounded-xl"
                disabled={sending}
                data-testid="message-input"
              />
              <Button
                type="submit"
                disabled={!newMessage.trim() || sending}
                className="bg-[#25D366] hover:bg-[#128C7E] text-white px-6 rounded-xl"
                data-testid="send-btn"
              >
                <Send className="h-5 w-5" />
              </Button>
            </div>
          </form>
        </>
      ) : (
        <div className="flex-1 flex items-center justify-center text-[#808080]">
          <div className="text-center">
            <MessageCircle className="h-20 w-20 mx-auto mb-4 opacity-20 text-[#25D366]" />
            <p className="text-xl font-medium">{t('مركز دعم WhatsApp', 'WhatsApp Support Center')}</p>
            <p className="text-sm mt-2 max-w-sm mx-auto">
              {t('اختر محادثة من القائمة أو أنشئ محادثة جديدة للتواصل مع العملاء', 'Pick a conversation from the list or create a new one to reach out to customers')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
